import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import {
  getPortalData,
  getVisitStageStates,
} from "../../../models/preAuthSlice";
import { errorMessage, successMessage } from "../../../utils/notificationUtils";
import { enhancementService, submitId } from "../../../apis/patientService";
import PortalSubmitModal from "../../../components/patient/PreAuth/PortalSubmitModal";
import { Form } from "antd";
import dayjs from "dayjs";
import { getPortalDataForAyushB } from "../../../models/ayushmanBSlice";
import RegistrationAyushB from "../../../components/patient/PreAuth/PreAuthAyush/RegistrationAyushB";
import { registerPatientPortal } from "../../../apis/ayushmanBService";

const STATUS = {
  INACTIVE: 4,
  COMPLETE: 8,
};

type RegistrationContainerAyushBProps = {
  nextStage: any;
  setMoveToNext: CallableFunction;
  claimId: string | null;
  onfetchStepper: () => void;
};
const RegistrationContainerAyushB: FC<RegistrationContainerAyushBProps> = (
  props
) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const { patients, preAuth, ayushBPreAuth } = useSelector((state: RootState) => state);
  const currentActiveStage =
    preAuth?.stepperData?.visitStages[preAuth?.currentStep];
  const currentStageDetails = preAuth?.stepperData?.currentVisitStage;

  const [manualStatusUpdateCount, setManualStatusUpdateCount] = useState<number>(0);
  const [editRegId, setEditRegId] = useState<boolean>(false);
  const [stageStatus, setStageStatus] = useState<number | null>(null);

  const [isSubmittedOnPortal, setIsSubmittedOnPortal] = useState<boolean>(false); //open is already submitted on portal
  const [isManuallyEnteredId, setIsManuallyEnteredId] = useState(false); //open enter id manually
  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);     // to check whether submitting api is going or not so that we can show loader acc. to that

  const isEditable = stageStatus !== STATUS.INACTIVE;

  const fetchPortalData = async () => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.id &&
      patients?.fetchBasicDetails?.visitInfoResponse?.id
    ) {
      const resp = await dispatch(
        getPortalDataForAyushB({
          caseId: patients?.fetchBasicDetails?.casesResponse?.id,
          visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
        })
      );

      if (getPortalData.rejected.match(resp)) {
        errorMessage("Please, Try again after sometime");
      }
    }
  };

  const fetchVisitStageIds = async () => {
    if (currentActiveStage?.visitStageId) {
      const resp = await dispatch(
        getVisitStageStates({ visitStageId: currentActiveStage?.visitStageId })
      );

      if (getVisitStageStates.rejected.match(resp)) {
        errorMessage("Please, Try again after sometime");
      }
    }
  };

      useEffect(() => {
        if (!currentActiveStage?.step || !currentStageDetails?.step) {
          setStageStatus(null);
          return;
        }
    
        if (currentActiveStage.step === currentStageDetails.step) {
          setStageStatus(currentStageDetails.stateId);
        } else if (currentActiveStage.step > currentStageDetails.step) {
          setStageStatus(STATUS.COMPLETE);
        } else {
          setStageStatus(STATUS.INACTIVE);
        }
      }, [currentActiveStage, currentStageDetails]);

  useEffect(() => {
    fetchPortalData();
    fetchVisitStageIds();
  }, [
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
    currentActiveStage?.visitStageId,
    manualStatusUpdateCount,
  ]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (
        patients?.fetchBasicDetails?.casesResponse?.id &&
        patients?.fetchBasicDetails?.visitInfoResponse?.id
      ) {
        if (
          preAuth?.portalData?.schemeRegistrationId == null &&
          preAuth?.portalData?.visitStagesName
            ?.toLowerCase()
            ?.toString() == "portal registration" &&
            preAuth?.portalData?.stateName?.toLowerCase()?.toString() ==
            "initiated"
        ) {
          fetchPortalData();
        }
      }
    }, 10000); 

    return () => {
      clearInterval(intervalId);
    };
  }, [
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
    preAuth?.portalData
  ]);

  const handleEditRegId = () => {
    setEditRegId(!editRegId);
  };

  const handleSubmitId = async (values: any) => {
    try {
      if (
        patients?.fetchBasicDetails?.casesResponse?.id &&
        patients?.fetchBasicDetails?.visitInfoResponse?.id &&
        patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId
      ) {
        const formData = {
          registrationId: values.registrationId,
          claimId: null,
        };
        const submitData = await submitId(
          patients?.fetchBasicDetails?.casesResponse?.id,
          patients?.fetchBasicDetails?.visitInfoResponse?.id,
          patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
          formData
        );
        if (submitData) {
          if (editRegId) {
            successMessage("Registration Id updated successfully");
          } else {
            successMessage(
              "Registration is Successful, Please Proceed further"
            );
          }
          await fetchPortalData();
          props?.onfetchStepper();
        }
      }
    } catch (err) {
      errorMessage("Please, Try again after sometime");
    }
  };

  const handleConfirmSubmition = () => {
    setOpen(true);
  };

  const handleOk = () => {
    setOpen(false);
  };

  const handleRegistrationCancel = () => {
    setOpen(false);
    setIsSubmittedOnPortal(false);
  };

  const handleSaveModalOption = async () => {
    try {
      if(patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId) {
        const registrationFormValues = await form.validateFields();
      const portalMetaData = await enhancementService.getPortalMetaData(
        patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId
      );
      const updatedData = {
        registrationId: registrationFormValues?.registrationId,
        caseId: patients?.fetchBasicDetails?.casesResponse?.id,
        visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
      };
      setIsLoading(true);
      await registerPatientPortal(updatedData);
      successMessage("Registered Successfully");
      setIsLoading(false);
      await fetchPortalData();
      props?.onfetchStepper();
      }
    } catch (err: any) {
      if(err.errorFields) {
        errorMessage("Please, Fill All details before submitting");
      }
      else {
        errorMessage("Please, Try again later");
      }
      setIsLoading(false);
    }
    setOpen(false);

  };

  const handleRegistration = () => {
    setOpen(true);
  }

  return (
    <>
      <RegistrationAyushB
        currentActiveStage={currentActiveStage}
        patientStore={patients?.fetchBasicDetails}
        stageAllState={preAuth?.stageAllState}
        currentStageDetails={currentStageDetails}
        setManualStatusUpdateCount={setManualStatusUpdateCount}
        schemePortalStatusList={preAuth?.schemePortalStatusList}
        portalData={ayushBPreAuth?.portalData}
        nextStage={props?.nextStage}
        setMoveToNext={props?.setMoveToNext}
        claimId={props?.claimId}
        editRegId={editRegId}
        stageStatus={stageStatus}
        isEditable={isEditable}
        handleEditRegId={handleEditRegId}
        handleSubmitId={handleSubmitId}
        handleRegistration={handleRegistration}
        form={form}
      />

      <PortalSubmitModal
        open={open}
        title={"Do You want to Register PreAuth?"}
        handleOk={handleOk}
        handleCancel={handleRegistrationCancel}
        handleSave={handleSaveModalOption}
      />
    </>
  );
};

export default RegistrationContainerAyushB;
